import { Point2D, Point2DWithOptions } from '@scale/shared/types/math';
import _ from 'lodash';
import { distance } from '@scale/shared/math';
import { extendLineSegment } from '@scale/shared/math/polylineMath';
import { PolygonVertices } from '@scale/shared/types/annotation';

/**
 * Heuristic for "closeness of point to line segment"
 * Mathematically, this is relates to the ellipse satisfying:
 *   foci at `p1` and `p2`
 *   passes through `point`
 * The value of the heuristic is a - c, where
 *   a is the semimajor axis
 *   c is the focal length
 * */
function pointLineHeuristic(point: Point2D, p1: Point2D, p2: Point2D) {
  return distance(p1, point) + distance(point, p2) - distance(p1, p2);
}

/**
 * Given a `point` and a polyline defined by `points`,
 * finds the edge of the polyline that is closest to point and returns the edge's index,
 * where the edge between points[i] and points[i + 1] has index i.
 * To support polygons, this will return `points.length - 1` if `points[0]` and `points[points.length - 1]` is the edge that is closest to `point`.
 *
 * If round is true, returns the index of the closest vertex instead.
 * If multiplePolygonVertices is set, return the closest edge of the multiple polygons and return the edge index.
 */
export const getPointIndex = (
  point: Point2D,
  points: Point2D[],
  opts: {
    closed?: boolean;
    round?: boolean;
    handleLength?: number;
    multiplePolygonVertices?: PolygonVertices[];
  } = {},
) => {
  if (opts.round) {
    return _.minBy(_.range(points.length), idx => distance(points[idx], point))!;
  }
  if (!_.isEmpty(opts.multiplePolygonVertices)) {
    let minV = Number.MAX_VALUE;
    let minIndex = -1;
    let fragmentPointsCount = 0;

    _.each(opts.multiplePolygonVertices, fragment => {
      const fPoints = fragment.vertices;
      for (let curIndex = 0; curIndex < fPoints.length; ++curIndex) {
        const isLastIndex = curIndex === fPoints.length - 1;
        const pointA = fPoints[curIndex];
        const pointB = isLastIndex ? fPoints[0] : fPoints[curIndex + 1];

        const dist = pointLineHeuristic(point, pointA, pointB);
        if (dist < minV) {
          minV = dist;
          minIndex = fragmentPointsCount + curIndex;
        }
      }

      fragmentPointsCount += fPoints.length;
    });

    return minIndex;
  } else if (opts.closed) {
    const possibleIndices = _.range(points.length);
    return _.minBy(possibleIndices, idx => {
      return pointLineHeuristic(point, points[idx], points[(idx + 1) % points.length]);
    })!;
  } else {
    const targetPoints = getTargetPathPoints(points, { handleLength: opts.handleLength });
    const possibleIndices = _.range(targetPoints.length - 1);
    const index = _.minBy(possibleIndices, idx => {
      return pointLineHeuristic(point, targetPoints[idx], targetPoints[idx + 1]);
    })!;
    return index - 1;
  }
};

const getSvgPathDForPolylineFragment = (vertices: Point2D[], opts?: { closed?: boolean }) => {
  const { closed = false } = opts || {};
  if (!vertices.length || vertices.length < 2) return;
  const path = _.map(
    vertices as (Point2D & { isBreak?: boolean })[], // support rendering broken polylines
    ({ x, y, isBreak }) => `${isBreak ? 'M' : 'L'} ${x} ${y}`,
  ).join(' ');
  const first = `M ${vertices[0].x} ${vertices[0].y}`;
  const last = closed ? 'Z' : '';
  return `${first}${path}${last}`;
};

export const getSvgPathDForPolyline = (
  vertices: Point2DWithOptions[],
  opts?: { closed?: boolean; multiplePolygonVertices?: PolygonVertices[] },
) => {
  const verticesList = opts?.multiplePolygonVertices ?? [
    {
      vertices,
    },
  ];
  return verticesList
    .map(({ vertices }) => {
      return getSvgPathDForPolylineFragment(vertices, opts);
    })
    .join(' ');
};

// TODO move getSvgPathDForPolylineArrow to svgUtils/getSvgPathDForPolylineArrow.ts
export const getSvgPathDForPolylineArrow = (vertices: Point2D[], opts: { arrowSize: number }) => {
  const eps = 1e-3;
  vertices = _.filter(
    vertices,
    (p, i) => i + 1 === vertices.length || distance(p, vertices[i + 1]) > eps,
  );
  const n = vertices.length;
  if (n < 2) return '';
  const prev = vertices[n - 2],
    pt = vertices[n - 1];
  const dir = Math.atan2(pt.y - prev.y, pt.x - prev.x);
  const steps = [];
  const [line1, line2] = [(Math.PI * 5) / 6, (-Math.PI * 5) / 6].map(ang => ({
    x: pt.x + opts.arrowSize * Math.cos(ang + dir),
    y: pt.y + opts.arrowSize * Math.sin(ang + dir),
  }));
  steps.push(`M${line1.x},${line1.y}`);
  steps.push(`L${pt.x},${pt.y}`);
  steps.push(`L${line2.x},${line2.y}`);
  steps.push('Z');
  return steps.join('');
};

/**
 * Returns a list of vertices corresponding to the "click target" of a path.
 * For polygons, this is exactly the input list of vertices.
 * For polylines, this is the input list of vertices, with two "handles" inserted at either end.
 * These handles are small extensions of length HANDLE_LENGTH of the last line segment.
 * Clicking on a handle will insert a vertex at the beginning or end of the vertex list, allowing the user to *extend* an existing line
 */
export const getTargetPathPoints = (
  points: Point2D[],
  opts: { closed?: boolean; handleLength?: number } = {},
): Point2D[] => {
  const handleLength = opts.handleLength ?? 0;
  if (opts.closed) return points;
  const n = points.length;
  return [
    extendLineSegment(points[1], points[0], handleLength),
    ...points,
    extendLineSegment(points[n - 2], points[n - 1], handleLength),
  ];
};

export const getHandlePathPoints = (
  points: Point2DWithOptions[],
  opts: { closed?: boolean; handleLength?: number } = {},
): (Point2DWithOptions & { isBreak?: boolean })[] => {
  const handleLength = opts.handleLength ?? 0;
  if (opts.closed) return points;
  const n = points.length;
  return _.compact([
    !points[0].anchored && extendLineSegment(points[1], points[0], handleLength),
    _.first(points)!,
    {
      ..._.last(points)!,
      isBreak: true,
    },
    !points[n - 1].anchored && extendLineSegment(points[n - 2], points[n - 1], handleLength),
  ]);
};
